import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DetailsAccordionSection from "../DetailsSection/DetailsSection";
import {
  apiResponseMiddleware,
  checkVisibility,
  getVisibleAndEditableFieldsConfig
} from "../../../services/ApiResponseMiddleware";
import DocumentComponentWrapper from "../DocumentComponent";
import UiConfigButton from "src/components/atomic/UiConfigButton/UiConfigButton";
import { getPageName } from "src/shared/utils/Global";
import { getTabConfig } from "src/services/apis/dashboardConfigService";
import { useBuildPanel } from "../BuilderPanel/useBuildPanel";

interface DynamicSubtabRendererProps {
  whiteLabelStyle?: any;
  activeTab: any;
  loanDetailsData?: any;
  isLenderDashboard: boolean;
  notify: any;
  token?: string;
  useLocation?: any;
  useNavigate?: any;
}

export default function DynamicSubtabRenderer({
  whiteLabelStyle,
  activeTab,
  loanDetailsData,
  isLenderDashboard,
  notify,
  token,
  useLocation,
  useNavigate
}: DynamicSubtabRendererProps) {
  const renderUIConfig = true;
  const { loanID } = useParams();

  const [loading, setLoading] = useState(false);

  const [formFields, setFormFields] = useState<any>(null);
  const [editableFields, setEditableFields] = useState<any>([]);
  const [configResponse, setConfigResponse] = useState<any>(null);
  const [formDetails, setFormDetails] = useState<any>({});

  const getConfig = async () => {
    const configResponse = await getTabConfig({
      activeTab,
      setLoading,
      token,
      notify
    });
    setConfigResponse(configResponse ?? {});
    setFormDetails(
      getVisibleAndEditableFieldsConfig(apiResponseMiddleware(configResponse))
    );
  };

  const onDataRefresh = () => {
    getConfig();
  };

  const { handleClickFinished, builderPanelNode } = useBuildPanel({
    token,
    pageName: getPageName(activeTab),
    activeTab,
    whiteLabelStyle,
    isLenderDashboard,
    staticContext: {
      loanApplicationData: {
        loanApplicationID: loanID,
        userID: loanDetailsData?.userID,
        resourceName: activeTab?.resourceName
      }
    },
    onDataRefresh
  });

  const renderDocumentCategory = (categoryKey: string, category: any) => {
    const documents = category?.documents || category?.docList || [];
    if (documents.length === 0) return null;

    return (
      <div key={categoryKey} className="mt-4">
        {category?.label && (
          <h3 className="text-sm text-black font-medium w-max border-b-2 border-indigo-500 py-2 pr-2 mb-4">
            {category.label}
          </h3>
        )}
        <div className="mt-4 flex flex-wrap -mb-2">
          {documents.map((document: any, docIndex: number) => {
            const isDocumentVisible = checkVisibility(document);
            if (!isDocumentVisible) return null;
            const configRuleData = configResponse?.data?.ruleData;
            return (
              <DocumentComponentWrapper
                key={
                  document?.id ||
                  document?.documentID ||
                  `${categoryKey}-${docIndex}`
                }
                doc={document}
                reloadParent={() => getConfig()}
                token={token}
                loanID={loanID}
                // isEditable={
                //   configRuleData?.tabPermissions?.features?.[
                //     TAB_ACTIONS.SCREENING_STATUS_DROPDOWN
                //   ]?.isVisible || false
                // }
                // pageName={pageName}
                isLenderDashboard={true}
                isAdditionalDocs={true}
                permissions={configRuleData?.pagePermission || {}}
                loanDetails={configResponse?.data?.loanDetails || {}}
                isMFL_LAP={false}
                isback={false}
                tabPermissions={configRuleData?.tabPermissions || {}}
                documentScreeningOptions={
                  configRuleData?.documentScreeningOptions || {}
                }
                mediaMetaData={document?.mediaMetaData}
                mediaID={document?.mediaID}
              />
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (activeTab) {
      getConfig();
    }
  }, [activeTab]);

  useEffect(() => {
    if (configResponse?.data) {
      const transformedSectionData = [];
      for (let index = 0; index < formDetails?.sections?.length; index++) {
        const sectionData = formDetails?.sections[index];
        const data = {
          title: sectionData.label,
          key: sectionData.id,
          id: sectionData.id,
          isEditable: checkVisibility(sectionData?.buttonList?.edit),
          buttonList: sectionData?.buttonList,
          fieldList: (sectionData?.fieldList || []).map((field: any) => ({
            ...field,
            name: field.label,
            value: typeof field.value === "object" ? "" : field.value,
            key: field.id,
            id: field.id,
            ...(field.nestedfieldList && {
              nestedfieldList: field.nestedfieldList.map(
                (nestedField: any) => ({
                  ...nestedField,
                  name: nestedField.label,
                  value:
                    typeof nestedField.value === "object"
                      ? ""
                      : nestedField.value,
                  key: nestedField.id,
                  id: nestedField.id
                })
              )
            })
          })),
          itemType: sectionData?.itemType,
          componentType: sectionData?.componentType,
          componentLabel: sectionData?.componentLabel
        };

        if (sectionData.sectionSequence)
          transformedSectionData[sectionData?.sectionSequence - 1] = data;
        else transformedSectionData.push(data);
      }
      setFormFields(transformedSectionData);
      setEditableFields(formDetails?.editableFieldsSectionWise || []);
    }
  }, [configResponse, formDetails]);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <div className="block p-4 md:overflow-auto">
        {Object.entries(formDetails?.buttons ?? {}).map(
          ([key, config]: [string, any]) => {
            return (
              <div className="mt-4 mr-4" key={key}>
                <UiConfigButton
                  key={key}
                  loanApplicationID={loanID!}
                  clientSideInput={{
                    loanApplicationID: loanID as string,
                    userID: loanDetailsData?.userID as string,
                    token: token as string
                  }}
                  buttonConfig={{
                    key: key,
                    ...config
                  }}
                  sectionID={activeTab.id}
                  tabName={getPageName(activeTab)}
                  isLenderDashboard={isLenderDashboard}
                  onClickFinished={handleClickFinished}
                  notifySuccess={false}
                />
              </div>
            );
          }
        )}

        {renderUIConfig &&
          formFields !== null &&
          formFields?.map((sectionData: any, idx: number) => (
            <DetailsAccordionSection
              key={sectionData.key}
              notify={notify}
              whiteLabelStyle={whiteLabelStyle}
              title={sectionData?.title}
              onDataRefresh={onDataRefresh}
              renderUIConfig={renderUIConfig}
              isEditable={renderUIConfig ? sectionData?.isEditable : false}
              items={sectionData?.fieldList}
              sectionData={sectionData || {}}
              editableFields={editableFields}
              userID={loanDetailsData?.userID}
              activeTab={activeTab}
              tabName={activeTab?.resourceName}
              loanApplicationID={loanID}
              resourceName={activeTab?.resourceName}
              resourceNameSingleSubmit={activeTab?.resourceNameSingleSubmit}
              pageName={getPageName(activeTab)}
              sectionID={sectionData?.id}
              isLenderPlatform={isLenderDashboard}
              token={token}
              reload={getConfig}
              graphQLResponse={configResponse?.data?.ruleData?.graphQLResponse}
              uiElements={configResponse?.data?.ruleData?.uiElements}
              useLocation={useLocation}
              useNavigate={useNavigate}
              showCustomButton={false}
              {...(renderUIConfig && {
                styles: {
                  heading: "pl-2",
                  keyValuePair: "pl-2",
                  keys: "font-normal"
                },
                isFirstSection: idx === 0,
                accordion: true
              })}
            />
          ))}

        {Object.entries(formDetails?.documents || {}).map(
          ([categoryKey, category]: [string, any]) =>
            renderDocumentCategory(categoryKey, category)
        )}
      </div>

      {builderPanelNode}
    </>
  );
}
